/**
 * The Charter HUD feature (M5 plan C18, order `FEATURE_ORDER.charterHud` 530, after the Wire 525; spec §15.4-§15.5,
 * §18 flows 1, 2 and 6). Runs only while the Reach and the strategic service are up.
 *
 * - **HUD** (`#charter-hud` under `ctx.hudRoot`): the jurisdiction chip, toasts (bounties, refusals, fines, gate),
 *   the "Being scanned" meter (C14's patrol scan), the combat-zone marker and prompt (zones within zones.json
 *   `hudRangeM`), the gate status line, the Isolated Era title card, and the resonance meter (tier ≥ 3, or the dev
 *   override). `frame` writes only `transform` and `textContent` on cached elements, and nothing when unchanged.
 * - **Flow 1 and the comms fines prompt (flow 2):** a docking refusal by the Charter policy (`SEV.clearanceDenied`
 *   with a policy reason, M2's ring) toasts "Clearance denied: wanted by Tethys (level 1)" and where to pay; within
 *   10 km of an Authority station it offers "Pay fines now", and a second docking request (F, spec §19) within
 *   `finesConfirmS` pays through the Career (`payCharterFines`).
 * - **Reconciliation (D-335 D4):** a hostile-standing refusal by the reconciliation faction (Holdfast) offers the
 *   comms-only amends (`charter/reconciliation.ts`); a second docking request within `finesConfirmS` pays it through
 *   the Charter career bridge. No docking is needed.
 * - **Station panels:** the Registry tab (`StationServicePanel`, Authority stations) and the state banner source for
 *   M2's station screen (`setStateBannerSource`).
 * - **Dev forcing** (`charterHudDev()`): every element can be forced for playtests (C20's panel binds it).
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { m2IdOfSite } from '../../bridge/m2/stations'
import type { SystemLayout } from '../../galaxy/layout/types'
import { fineTotal, type PayFinesResult } from '../../charter/fines'
import { reconciliationOfferText, reconciliationResultText } from '../../charter/reconciliation'
import { activeCharter, canPayFinesHere, payCharterFines } from '../../charter/subsystem'
import { ZONES_CONFIG, zonePrompt } from '../../charter/zones'
import { logger } from '../../core/logger'
import { DOCKING_POLICY_REASON } from '../../docking/clearance'
import { createHudCamera, createScreenPoint, projectWorld, setHudCamera, type HudCamera } from '../../hud/projection'
import type { StationServiceRegistry } from '../../reach/ports'
import { firstReadableSpace, SEV, spaceEventSlot, type SpaceEventRing } from '../../space/events'
import type { StrategicClient } from '../../strategic/client'
import { FACTION_IDS, type ClaimView, type GateView, type RelationsView, type StationView } from '../../strategic/types'
import { createRegistryPanel, payResultText } from '../../ui/charter/Registry'
import { setStateBannerSource } from '../../ui/charter/StateBanner'
import { setBannerClaimSource } from '../../hud/reach/labels'
import { Pos64 } from '../../world/components'
import {
  bountyToasts,
  clearanceRefusalText,
  clearToasts,
  createCharterHudState,
  createCharterHudView,
  createHudZones,
  enteredIsolation,
  fillHudZones,
  finesOfferText,
  gateStatusText,
  gateToastText,
  jurisdictionChipText,
  jurisdictionTone,
  loadCharterHudConfig,
  nearestHudZone,
  pushToast,
  stepCharterHud,
  type CharterHudConfig,
  type CharterHudElements,
  type CharterHudState,
  type HudZoneInput,
  type ToastKind,
} from './hudModel'
import { meterVisible, resetResonance, sampleResonance } from './resonance'
import { resonanceMeterFitted } from '../../story-link/missions/m2'
import { charterCareerBridge } from '../charterCareer/feature'
import './hud.css'

/** Forcing hooks for playtests and the dev panel (C20). Null clears a force. */
export interface CharterHudDev {
  toast(text: string, kind?: ToastKind): void
  scan(progress: number | null): void
  zone(prompt: string | null, distM?: number): void
  isolatedCard(): void
  /** The resonance meter's visibility regardless of the story tier. */
  meter(on: boolean | null): void
  /** A stability shown by the meter while forced (the worker hides it before tier 3). */
  stability(value: number | null): void
  gateStatus(text: string | null): void
  chip(text: string | null): void
}

export interface CharterHudFeature extends GameFeature {
  readonly state: CharterHudState | null
  readonly dev: CharterHudDev
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

export interface CharterHudOptions {
  config?: CharterHudConfig
  /** Builds the elements (default: real DOM under `ctx.hudRoot`); tests pass fakes. */
  mount?: (ctx: GameContext) => { els: CharterHudElements; host: HTMLElement | null }
  viewport?: () => { width: number; height: number }
}

/** What the HUD reads of the strategic client (`StrategicClient` satisfies it). */
export type CharterHudClient = Pick<
  StrategicClient,
  'onSystemView' | 'onManifest' | 'onRelations' | 'onGate' | 'relations' | 'systemView' | 'manifest' | 'clock'
>

function div(doc: Document, cls: string, parent: HTMLElement, text = ''): HTMLDivElement {
  const e = doc.createElement('div')
  e.className = cls
  if (text !== '') e.textContent = text
  parent.appendChild(e)
  return e
}

/** Builds `#charter-hud` and returns its elements. */
export function mountCharterHud(parent: HTMLElement, cfg: CharterHudConfig): { els: CharterHudElements; host: HTMLElement } {
  const doc = parent.ownerDocument
  const root = div(doc, 'ch-root', parent)
  root.id = 'charter-hud'
  root.dataset.testid = 'charter-hud'
  // H11: the chip, the gate line and the toast are status-stack chips (hud/status.ts places them, top centre).
  const chip = div(doc, 'ch-chip hud-chip', root)
  chip.dataset.testid = 'charter-chip'
  const chipText = div(doc, 'ch-chip__text', chip)
  const gate = div(doc, 'ch-gate hud-chip', root)
  gate.dataset.testid = 'charter-gate'
  const gateText = div(doc, 'ch-gate__text', gate)
  const toast = div(doc, 'ch-toast hud-chip', root)
  toast.dataset.testid = 'charter-toast'
  toast.setAttribute('role', 'status')
  toast.setAttribute('aria-live', 'polite')
  const toastText = div(doc, 'ch-toast__text', toast)
  const scan = div(doc, 'ch-scan', root)
  scan.dataset.testid = 'charter-scan'
  div(doc, 'ch-scan__label', scan, 'Being scanned')
  const scanTrack = div(doc, 'ch-scan__track', scan)
  const scanBar = div(doc, 'ch-scan__bar', scanTrack)
  const zone = div(doc, 'ch-zone', root)
  zone.dataset.testid = 'charter-zone'
  div(doc, 'ch-zone__mark', zone)
  const zonePrompt = div(doc, 'ch-zone__prompt', zone)
  const zoneDist = div(doc, 'ch-zone__dist', zone)
  const card = div(doc, 'ch-card', root)
  card.dataset.testid = 'charter-isolated-card'
  div(doc, 'ch-card__title', card, 'The Isolated Era')
  div(doc, 'ch-card__sub', card, 'The gate is closed. The Reach stands alone.')
  const meter = div(doc, 'ch-meter', root)
  meter.dataset.testid = 'charter-resonance'
  div(doc, 'ch-meter__label', meter, 'Resonance')
  const gauge = div(doc, 'ch-meter__gauge', meter)
  const meterFill = div(doc, 'ch-meter__fill', gauge)
  for (const m of cfg.meter.marks) {
    const mark = div(doc, 'ch-meter__mark', gauge)
    mark.style.bottom = `${m}%`
    mark.dataset.value = String(m)
  }
  const meterValue = div(doc, 'ch-meter__value', meter)
  const trend = div(doc, 'ch-meter__trend', meter)
  const meterBars: HTMLDivElement[] = []
  for (let i = 0; i < cfg.meter.trendDays; i++) meterBars.push(div(doc, 'ch-meter__bar', trend))
  const meterTrend = div(doc, 'ch-meter__delta', meter)
  const hidden = [chip, gate, toast, scan, zone, card, meter]
  for (const h of hidden) h.style.transform = 'scale(0)'
  return {
    host: root,
    els: { root, chip, chipText, toast, toastText, scan, scanBar, zone, zonePrompt, zoneDist, card, gate, gateText, meter, meterFill, meterValue, meterTrend, meterBars },
  }
}

/** Registries the Registry panel already went into (the registry has no removal). */
const registered = new WeakSet<StationServiceRegistry>()

let live: CharterHudFeature | null = null

/** The running Charter HUD's dev hooks (C20's panel and the debug API), or null before it runs. */
export function charterHudDev(): CharterHudDev | null {
  return live !== null && live.state !== null ? live.dev : null
}

/**
 * The strategic station view behind M2's docked station id, from the client's cached system view. Site ids map to
 * M2's through `m2IdOfSite` over the live `layout` (R3-1 sweep: the home Hub is `hub`, not `sys_07_st0`).
 */
export function stationViewFor(
  client: Pick<StrategicClient, 'systemView'>,
  stationId: string,
  layout: Pick<SystemLayout, 'stations' | 'home'> | null = null,
): StationView | null {
  const v = client.systemView
  if (v === null) return null
  for (const s of v.stations) if (s.id === stationId || m2IdOfSite(s.id, layout) === stationId) return s
  return null
}

export function createCharterHudFeature(opts: CharterHudOptions = {}): CharterHudFeature {
  const cfg = opts.config ?? loadCharterHudConfig()
  const cam: HudCamera = createHudCamera()
  const pt = createScreenPoint()
  const distOut = new Float64Array(1)
  const zones = createHudZones()
  const bounties = new Float64Array(FACTION_IDS.length)
  let state: CharterHudState | null = null
  let view: ReturnType<typeof createCharterHudView> | null = null
  let host: HTMLElement | null = null
  let client: CharterHudClient | null = null
  let ctxRef: GameContext | null = null
  let ring: SpaceEventRing | null = null
  let cursor = 0
  let systemId: string | null = null
  let claim: ClaimView | null = null
  let relations: RelationsView | null = null
  let relationsPrimed = false
  let gate: GateView | null = null
  let chipDirty = true
  let width = 1
  let height = 1
  let listening = false
  const offs: (() => void)[] = []
  /** Seconds left in which a second hostile-standing refusal accepts the reconciliation offer (D-335 D4). */
  let amendsConfirmS = 0
  // Dev forces.
  let forceScan: number | null = null
  let forceZone: string | null = null
  let forceZoneDist = 0
  let forceMeter: boolean | null = null
  let forceStability: number | null = null
  let forceGate: string | null = null
  let forceChip: string | null = null

  const readViewport = (): void => {
    const size = opts.viewport?.() ?? { width: window.innerWidth, height: window.innerHeight }
    width = size.width
    height = size.height
  }

  function toast(text: string, kind: ToastKind): void {
    if (state !== null) pushToast(state.toasts, text, kind)
  }

  function sampleGate(): void {
    if (state === null) return
    const day = client?.clock?.day ?? 0
    sampleResonance(state.resonance, day, forceStability ?? gate?.stability ?? null)
  }

  function onGate(next: GateView): void {
    if (state === null) return
    const now = client?.clock?.time ?? 0
    const text = gateToastText(gate, next, now)
    if (text !== null) toast(text, next.stage === 'normal' && !next.isolation ? 'info' : 'warn')
    if (enteredIsolation(gate, next)) state.cardS = cfg.isolatedCardS
    gate = next
    if (forceGate === null) state.gateStatus = next.stage === 'normal' && !next.isolation ? '' : gateStatusText(next)
    sampleGate()
  }

  function onRelations(v: RelationsView): void {
    relations = v
    chipDirty = true
    if (state !== null) bountyToasts(state.toasts, bounties, v, relationsPrimed)
    relationsPrimed = true
  }

  function setZones(list: readonly HudZoneInput[]): void {
    fillHudZones(zones, list, zonePrompt)
  }

  function onSystemView(v: { systemId: string; claim: ClaimView; zones: readonly HudZoneInput[] }): void {
    if (v.systemId !== systemId) return
    claim = v.claim
    chipDirty = true
    setZones(v.zones)
  }

  function enterSystem(id: string | null): void {
    systemId = id
    claim = null
    zones.count = 0
    chipDirty = true
    const c = client
    if (c === null || id === null) return
    const m = c.manifest
    if (m !== null && m.systemId === id) setZones(m.zones)
    const v = c.systemView
    if (v !== null) onSystemView(v)
  }

  function payFines(ctx: GameContext): Promise<PayFinesResult> {
    const rt = activeCharter()
    const reach = ctx.reach
    if (rt === null || reach === null) return Promise.resolve({ status: 'failed', error: 'the Charter is not running' })
    return payCharterFines(rt, reach.career).then((r) => {
      chipDirty = true
      // The paid toast says every bounty cleared; the next relations view must not repeat it.
      if (r.status === 'paid') bounties.fill(0)
      return r
    })
  }

  function onRefusal(ctx: GameContext, reason: number): void {
    const s = state
    if (s === null) return
    const rt = activeCharter()
    const here = rt !== null && canPayFinesHere(rt, ctx.sim)
    const total = Math.round(fineTotal(rt?.relations ?? relations))
    if (reason === DOCKING_POLICY_REASON.wanted && here && total > 0 && s.finesConfirmS > 0) {
      s.finesConfirmS = 0
      void payFines(ctx).then((r) => toast(payResultText(r), r.status === 'paid' ? 'info' : 'warn'))
      return
    }
    const r = rt?.policy.lastRefusal
    const faction = r !== undefined && r.faction >= 0 ? FACTION_IDS[r.faction] : null
    const amends = reason === DOCKING_POLICY_REASON.hostileStanding ? offerAmends(faction) : null
    if (amends === 'accepted') return
    toast(clearanceRefusalText(reason, faction, r?.level ?? 1), 'alert')
    if (amends !== null) toast(amends, 'warn')
    if (reason !== DOCKING_POLICY_REASON.wanted || total <= 0) return
    toast(finesOfferText(total, here), 'warn')
    if (here) s.finesConfirmS = cfg.finesConfirmS
  }

  /**
   * The comms-only reconciliation (D-335 D4): on a hostile-standing refusal by the offering faction, a first request
   * offers it (returns the comms line); a second within `finesConfirmS` accepts it through the Career ('accepted').
   * Null when no offer is open for that faction.
   */
  function offerAmends(faction: string | null): string | null {
    const b = charterCareerBridge()
    const offer = b?.reconciliationOffer() ?? null
    if (b === null || offer === null || offer.faction !== faction) return null
    if (amendsConfirmS > 0) {
      amendsConfirmS = 0
      const r = b.reconcile()
      chipDirty = true
      toast(reconciliationResultText(r, offer.faction), r.ok ? 'info' : 'warn')
      return 'accepted'
    }
    amendsConfirmS = cfg.finesConfirmS
    return reconciliationOfferText(offer)
  }

  function readRefusals(ctx: GameContext): void {
    const r = ring
    if (r === null) return
    const head = r.head
    for (let i = firstReadableSpace(r, cursor); i < head; i++) {
      const k = spaceEventSlot(i)
      if (r.type[k] !== SEV.clearanceDenied) continue
      const reason = r.b[k]
      if (reason === DOCKING_POLICY_REASON.wanted || reason === DOCKING_POLICY_REASON.hostileStanding) onRefusal(ctx, reason)
    }
    cursor = head
  }

  const dev: CharterHudDev = {
    toast(text: string, kind: ToastKind = 'info'): void {
      toast(text, kind)
    },
    scan(progress: number | null): void {
      forceScan = progress
    },
    zone(prompt: string | null, distM = 12_000): void {
      forceZone = prompt
      forceZoneDist = distM
    },
    isolatedCard(): void {
      if (state !== null) state.cardS = cfg.isolatedCardS
    },
    meter(on: boolean | null): void {
      forceMeter = on
    },
    stability(value: number | null): void {
      forceStability = value
      sampleGate()
    },
    gateStatus(text: string | null): void {
      forceGate = text
      if (state !== null) state.gateStatus = text ?? (gate === null || (gate.stage === 'normal' && !gate.isolation) ? '' : gateStatusText(gate))
    },
    chip(text: string | null): void {
      forceChip = text
      chipDirty = true
    },
  }

  const feature: CharterHudFeature = {
    name: 'charterHud',
    order: FEATURE_ORDER.charterHud,
    dev,
    get state(): CharterHudState | null {
      return state
    },

    init(ctx: GameContext): void {
      const reach = ctx.reach
      const c = ctx.services.maybe('strategic')
      if (reach === null || c === undefined) return
      client = c
      ctxRef = ctx
      const mounted = opts.mount?.(ctx) ?? mountCharterHud(ctx.hudRoot, cfg)
      host = mounted.host
      view = createCharterHudView(mounted.els, cfg, ZONES_CONFIG.hudRangeM)
      state = createCharterHudState(cfg)
      state.active = true
      const space = ctx.services.maybe('m2')?.space ?? null
      ring = space?.events ?? null
      cursor = ring?.head ?? 0
      offs.push(
        c.onRelations(onRelations),
        c.onGate(onGate),
        c.onSystemView(onSystemView),
        c.onManifest((m) => {
          if (m.systemId === systemId) setZones(m.zones)
        }),
      )
      if (c.relations !== null) onRelations(c.relations)
      if (!registered.has(reach.services)) {
        registered.add(reach.services)
        reach.services.register(
          createRegistryPanel({
            relations: () => client?.relations ?? relations,
            onRelations: (fn) => client?.onRelations(fn) ?? ((): void => {}),
            wallet: () => ctxRef?.reach?.career.credits() ?? 0,
            pay: () => (ctxRef === null ? Promise.resolve({ status: 'failed', error: 'no game' }) : payFines(ctxRef)),
          }),
        )
      }
      // The owner line names a corporation claim from the system's claim view (D-424 D5).
      setStateBannerSource((id) => {
        const st = client === null ? null : stationViewFor(client, id, reach.session.layout)
        return st === null ? null : { ...st, corp: client?.systemView?.claim.corp ?? null }
      })
      // CL-7: the Reach system banner adds "· unclaimed" once this system's claim view says no one holds it.
      setBannerClaimSource((id) => {
        const v = client?.systemView ?? null
        return v === null || v.systemId !== id ? null : v.claim.holder !== null
      })
      readViewport()
      if (opts.viewport === undefined) {
        window.addEventListener('resize', readViewport)
        listening = true
      }
      live = feature
      logger.debug('Charter HUD: mounted')
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const s = state
      if (s === null || view === null) return
      stepCharterHud(s, f.paused ? 0 : f.dt, cfg)
      if (amendsConfirmS > 0) amendsConfirmS = Math.max(0, amendsConfirmS - (f.paused ? 0 : f.dt))
      const sys = ctx.reach?.session.layout?.systemId ?? null
      if (sys !== systemId) enterSystem(sys)
      if (chipDirty) {
        chipDirty = false
        s.chip = forceChip ?? jurisdictionChipText(claim, relations)
        s.chipTone = forceChip !== null ? 'lawful' : jurisdictionTone(claim, relations)
      }
      const career = ctx.services.maybe('m2')?.career() ?? null
      s.meterVisible = meterVisible(career?.storyTier ?? 0, forceMeter, resonanceMeterFitted(career))
      const rt = activeCharter()
      if (forceScan !== null) {
        s.scanActive = true
        s.scanProgress = forceScan
      } else {
        s.scanActive = rt !== null && rt.scan.active
        s.scanProgress = rt?.scan.progress ?? 0
      }
      const p = ctx.sim.playerEid
      if (forceZone !== null) {
        s.zonePrompt = forceZone
        s.zoneDistM = forceZoneDist
        s.zoneOnScreen = true
        s.zoneX = width * 0.5
        s.zoneY = height * 0.3
      } else if (p >= 0 && zones.count > 0) {
        const k = nearestHudZone(zones, Pos64.x[p], Pos64.y[p], Pos64.z[p], ZONES_CONFIG.hudRangeM, distOut)
        if (k >= 0) {
          const camera = ctx.camera
          setHudCamera(cam, ctx.rig.pos, camera.position, camera.quaternion, camera.fov, camera.zoom, width, height)
          projectWorld(cam, zones.x[k], zones.y[k], zones.z[k], 24, pt)
          s.zonePrompt = zones.prompt[k]
          s.zoneDistM = distOut[0]
          s.zoneOnScreen = pt.onScreen
          s.zoneX = pt.x
          s.zoneY = pt.y
        } else s.zonePrompt = ''
      } else s.zonePrompt = ''
      if (!f.paused) readRefusals(ctx)
      view.sync(s)
    },

    dispose(): void {
      for (const off of offs) off()
      offs.length = 0
      if (listening) window.removeEventListener('resize', readViewport)
      listening = false
      setStateBannerSource(null)
      setBannerClaimSource(null)
      if (state !== null) {
        clearToasts(state.toasts)
        resetResonance(state.resonance)
      }
      host?.remove()
      host = null
      view = null
      state = null
      client = null
      ctxRef = null
      ring = null
      systemId = null
      claim = null
      relations = null
      relationsPrimed = false
      gate = null
      bounties.fill(0)
      if (live === feature) live = null
    },
  }
  return feature
}

const feature: CharterHudFeature = createCharterHudFeature()

export default feature
